<template>
  <div class="price-check-reports-page">
    <div class="reports-header">
      <div>
        <h3>Re-shopping Performance Report</h3>
      </div>
      <b-button variant="outline-primary" @click="goBack">Back</b-button>
    </div>

    <section class="filter-panel">
      <div class="filter-grid">
        <div class="filter-field">
          <label>Client</label>
          <select v-model="filters.client" :disabled="isLoading">
            <option value="">All Clients</option>
            <option v-for="client in clientOptions" :key="client" :value="client">
              {{ client }}
            </option>
          </select>
        </div>

        <div class="filter-field">
          <label>Currency</label>
          <select v-model="filters.currency" :disabled="isLoading">
            <option v-for="currency in currencyOptions" :key="currency" :value="currency">
              {{ currency }}
            </option>
          </select>
        </div>

        <div class="filter-field">
          <label>Year</label>
          <select v-model="filters.year" :disabled="isLoading">
            <option value="">All Years</option>
            <option v-for="year in yearOptions" :key="year" :value="String(year)">
              {{ year }}
            </option>
          </select>
        </div>

        <div class="filter-field">
          <label>Month</label>
          <select v-model="filters.month" :disabled="isLoading">
            <option value="">All Months</option>
            <option v-for="month in monthOptions" :key="month.value" :value="String(month.value)">
              {{ month.label }}
            </option>
          </select>
        </div>

        <div class="filter-actions">
          <b-button variant="primary" :disabled="isLoading" @click="applyFilters">Submit</b-button>
          <b-button variant="outline-primary" :disabled="isLoading" @click="resetFilters">Reset</b-button>
        </div>
      </div>
    </section>

    <section class="kpi-grid" aria-label="Price check report KPIs">
      <div v-for="card in kpiCards" :key="card.key" class="kpi-card">
        <div class="kpi-icon" :class="card.tone">
          <b-icon :icon="card.icon" />
        </div>
        <div class="kpi-copy">
          <span>{{ card.title }}</span>
          <strong :class="{ 'savings-value': card.key === 'PotentialSavings' }">
            <span v-if="isLoading" class="kpi-loading"></span>
            <template v-else-if="card.partitions">
              <span
                v-for="partition in card.partitions"
                :key="partition.label"
                class="partition-line"
              >
                <b>{{ partition.label }}</b>
                <em>{{ partition.value }}</em>
              </span>
            </template>
            <template v-else>{{ card.value }}</template>
          </strong>
          <small>{{ card.description }}</small>
        </div>
      </div>
    </section>

    <section class="reports-panel">
      <div class="panel-title">
        <div>
          <h4>Monthly Booking Reports</h4>
          <p>Booking performance and potential savings</p>
        </div>
        <span v-if="!isLoading && !errorMessage">{{ pagination.total_records }} result{{ pagination.total_records === 1 ? '' : 's' }}</span>
      </div>

      <div v-if="isLoading" class="table-skeleton" aria-label="Loading reports">
        <span v-for="item in 6" :key="item"></span>
      </div>
      <p v-else-if="errorMessage" class="error-text">{{ errorMessage }}</p>
      <div v-else-if="reportRows.length" class="report-table-wrap">
        <table class="report-table">
          <thead>
            <tr>
              <th
                v-for="column in tableColumns"
                :key="column.key"
                :class="columnClass(column.key)"
              >
                {{ tableLabel(column) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowIndex) in reportRows" :key="rowKey(row, rowIndex)">
              <td
                v-for="column in tableColumns"
                :key="column.key"
                :class="columnClass(column.key)"
              >
                <template v-if="column.key === 'ProfitPercentage'">
                  <div class="percentage-cell partition-percentage-cell">
                    <span
                      v-for="partition in percentagePartitions(row[column.key])"
                      :key="partition.label"
                    >
                      <b>{{ partition.label }}</b>
                      <em>{{ partition.value }}</em>
                      <i>
                        <b :style="{ width: percentageBarWidth(partition.raw) }"></b>
                      </i>
                    </span>
                  </div>
                </template>
                <div
                  v-else-if="column.key === 'TotalProfit'"
                  class="partition-cell"
                >
                  <span
                    v-for="partition in savingsPartitions(row[column.key])"
                    :key="partition.label"
                    :class="{
                      'savings-positive': numericValue(partition.raw) > 0,
                      'savings-zero': numericValue(partition.raw) === 0,
                      'savings-negative': numericValue(partition.raw) < 0,
                    }"
                  >
                    <b>{{ partition.label }}</b>
                    <em>{{ partition.value }}</em>
                  </span>
                </div>
                <span
                  v-else
                  :class="{
                    'savings-positive': column.key === 'TotalProfit' && numericValue(row[column.key]) > 0,
                    'savings-zero': column.key === 'TotalProfit' && numericValue(row[column.key]) === 0,
                    'savings-negative': column.key === 'TotalProfit' && numericValue(row[column.key]) < 0,
                  }"
                >
                  {{ formatCell(row[column.key], column.key) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="!isLoading && !errorMessage && pagination.total_records" class="pagination-bar">
        <div class="pagination-summary">
          Showing {{ paginationStart }}-{{ paginationEnd }} of {{ formatNumber(pagination.total_records) }}
        </div>
        <div class="pagination-controls">
          <label>
            Rows
            <select v-model.number="pagination.page_size" @change="changePageSize">
              <option v-for="size in pageSizeOptions" :key="size" :value="size">
                {{ size }}
              </option>
            </select>
          </label>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="pagination.page <= 1"
            @click="changePage(pagination.page - 1)"
          >
            Previous
          </b-button>
          <span>Page {{ pagination.page }} of {{ pagination.total_pages }}</span>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="pagination.page >= pagination.total_pages"
            @click="changePage(pagination.page + 1)"
          >
            Next
          </b-button>
        </div>
      </div>
      <p v-if="!isLoading && !errorMessage && !reportRows.length" class="status-text">No booking reports match the selected filters.</p>
    </section>
  </div>
</template>

<script>
import axios from '@axios'
import { BButton, BIcon } from 'bootstrap-vue'

export default {
  components: {
    BButton,
    BIcon,
  },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
      reportRows: [],
      tableColumns: this.defaultColumns(),
      kpis: this.defaultKpis(),
      reportRequestId: 0,
      pageSizeOptions: [10, 25, 50, 100],
      pagination: {
        page: 1,
        page_size: 10,
        total_records: 0,
        total_pages: 0,
      },
      clientOptions: [],
      currencyOptions: ['USD'],
      yearOptions: [],
      monthOptions: [
        { value: 1, label: 'Jan' },
        { value: 2, label: 'Feb' },
        { value: 3, label: 'Mar' },
        { value: 4, label: 'Apr' },
        { value: 5, label: 'May' },
        { value: 6, label: 'Jun' },
        { value: 7, label: 'Jul' },
        { value: 8, label: 'Aug' },
        { value: 9, label: 'Sep' },
        { value: 10, label: 'Oct' },
        { value: 11, label: 'Nov' },
        { value: 12, label: 'Dec' },
      ],
      filters: {
        client: '',
        currency: 'USD',
        year: '',
        month: '',
      },
    }
  },
  computed: {
    kpiCards() {
      const currency = this.kpis.Currency || this.filters.currency || 'USD'
      return [
        {
          key: 'TotalBookings',
          title: 'Total Bookings',
          value: this.formatNumber(this.kpis.TotalBookings),
          description: 'All bookings analyzed',
          icon: 'calendar',
          tone: 'primary',
        },
        {
          key: 'CheaperBookings',
          title: 'Cheaper Bookings',
          value: this.formatNumber(this.kpis.CheaperBookings),
          description: 'Lower prices found',
          icon: 'check-circle',
          tone: 'success',
        },
        {
          key: 'OpportunityRate',
          title: 'Opportunity Rate',
          value: this.formatPercent(this.kpis.OpportunityRate),
          description: 'Bookings with savings',
          icon: 'percent',
          tone: 'info',
        },
        {
          key: 'TotalOriginalCost',
          title: 'Original Cost',
          value: this.formatCurrency(this.kpis.TotalOriginalCost, currency),
          description: 'Cheaper bookings only',
          icon: 'credit-card',
          tone: 'money',
        },
        {
          key: 'PotentialSavings',
          title: 'Potential Savings',
          partitions: this.savingsPartitions(this.kpis.PotentialSavings),
          description: 'Original - re-shop price',
          icon: 'cash',
          tone: 'success',
        },
        {
          key: 'SavingsPercentage',
          title: 'Savings Percentage',
          partitions: this.percentagePartitions(this.kpis.SavingsPercentage),
          description: 'Of original cost',
          icon: 'graph-up',
          tone: 'trend',
        },
      ]
    },
    paginationStart() {
      if (!this.pagination.total_records) {
        return 0
      }
      return ((this.pagination.page - 1) * this.pagination.page_size) + 1
    },
    paginationEnd() {
      return Math.min(
        this.pagination.page * this.pagination.page_size,
        this.pagination.total_records,
      )
    },
  },
  mounted() {
    this.fetchReport()
  },
  methods: {
    defaultKpis(currency = 'USD') {
      return {
        TotalBookings: 0,
        CheaperBookings: 0,
        OpportunityRate: 0,
        TotalOriginalCost: 0,
        PotentialSavings: {
          refundable: 0,
          non_refundable: 0,
        },
        SavingsPercentage: {
          refundable: 0,
          non_refundable: 0,
        },
        Currency: currency,
      }
    },
    defaultColumns() {
      return [
        { key: 'Year', label: 'Year' },
        { key: 'Month', label: 'Month' },
        { key: 'Currency', label: 'Currency' },
        { key: 'TotalBookings', label: 'Total Bookings' },
        { key: 'CheaperBookings', label: 'Cheaper Bookings' },
        { key: 'TotalOriginalCost', label: 'Original Cost' },
        { key: 'TotalRefundableCost', label: 'Refundable Cost' },
        { key: 'TotalNonRefundableCost', label: 'Non Refundable Cost' },
        { key: 'TotalProfit', label: 'Potential Savings' },
        { key: 'ProfitPercentage', label: 'Savings %' },
      ]
    },
    applyFilters() {
      if (!this.isLoading) {
        this.pagination.page = 1
        this.fetchReport()
      }
    },
    resetFilters() {
      if (this.isLoading) {
        return
      }
      this.filters = {
        client: '',
        currency: 'USD',
        year: '',
        month: '',
      }
      this.pagination.page = 1
      this.pagination.page_size = 10
      this.fetchReport()
    },
    fetchReport() {
      const requestId = this.reportRequestId + 1
      this.reportRequestId = requestId
      this.isLoading = true
      this.errorMessage = ''
      this.kpis = this.defaultKpis(this.filters.currency)
      this.reportRows = []
      axios.post('price-check/bookings/report', {
        client: this.filters.client,
        currency: this.filters.currency,
        year: this.filters.year,
        month: this.filters.month,
        page: this.pagination.page,
        page_size: this.pagination.page_size,
      })
        .then(response => {
          if (requestId !== this.reportRequestId) {
            return
          }
          const data = response.data || {}
          const filterOptions = data.filters || {}
          this.clientOptions = Array.isArray(filterOptions.clients) ? filterOptions.clients : []
          this.currencyOptions = this.normalizeCurrencyOptions(filterOptions.currencies)
          this.yearOptions = Array.isArray(filterOptions.years) ? filterOptions.years : []
          this.monthOptions = Array.isArray(filterOptions.months) && filterOptions.months.length
            ? filterOptions.months
            : this.monthOptions
          this.tableColumns = Array.isArray(data.columns) && data.columns.length
            ? data.columns
            : this.defaultColumns()
          this.kpis = data.kpis && typeof data.kpis === 'object'
            ? { ...this.defaultKpis(this.filters.currency), ...data.kpis }
            : this.defaultKpis(this.filters.currency)
          this.reportRows = Array.isArray(data.results) ? data.results : []
          this.pagination = {
            ...this.pagination,
            ...this.normalizePagination(data.pagination),
          }
        })
        .catch(error => {
          if (requestId !== this.reportRequestId) {
            return
          }
          const message = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Unable to generate report.'
          const details = error.response && error.response.data && error.response.data.error
            ? ` ${error.response.data.error}`
            : ''
          this.errorMessage = `${message}${details}`
          this.reportRows = []
          this.tableColumns = this.defaultColumns()
          this.kpis = this.defaultKpis(this.filters.currency)
          this.pagination = {
            ...this.pagination,
            total_records: 0,
            total_pages: 0,
          }
        })
        .finally(() => {
          if (requestId !== this.reportRequestId) {
            return
          }
          this.isLoading = false
        })
    },
    normalizeCurrencyOptions(currencies) {
      const values = Array.isArray(currencies) ? currencies.filter(Boolean) : []
      if (!values.includes('USD')) {
        values.unshift('USD')
      }
      return values
    },
    normalizePagination(pagination) {
      const payload = pagination && typeof pagination === 'object' ? pagination : {}
      return {
        page: Math.max(1, Number(payload.page) || 1),
        page_size: Math.max(1, Number(payload.page_size) || this.pagination.page_size),
        total_records: Math.max(0, Number(payload.total_records) || 0),
        total_pages: Math.max(0, Number(payload.total_pages) || 0),
      }
    },
    changePage(page) {
      if (this.isLoading || page === this.pagination.page) {
        return
      }
      this.pagination.page = Math.max(1, Math.min(page, this.pagination.total_pages || 1))
      this.fetchReport()
    },
    changePageSize() {
      if (this.isLoading) {
        return
      }
      this.pagination.page = 1
      this.fetchReport()
    },
    formatCell(value, key) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      if (Number.isNaN(value)) {
        return '-'
      }
      if (['TotalOriginalCost', 'TotalRefundableCost', 'TotalNonRefundableCost'].includes(key)) {
        return this.formatCurrency(value, this.kpis.Currency || this.filters.currency)
      }
      if (key === 'ProfitPercentage') {
        return value
      }
      if (['TotalBookings', 'CheaperBookings'].includes(key)) {
        return this.formatNumber(value)
      }
      if (typeof value === 'number') {
        return value.toLocaleString(undefined, {
          maximumFractionDigits: 2,
          minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
        })
      }
      return value
    },
    tableLabel(column) {
      const labels = {
        TotalOriginalCost: 'Original Cost',
        TotalRefundableCost: 'Refundable Cost',
        TotalNonRefundableCost: 'Non Refundable Cost',
        TotalProfit: 'Potential Savings',
        ProfitPercentage: 'Savings %',
      }
      return labels[column.key] || column.label
    },
    rowKey(row, index) {
      return `${row.Year || 'year'}-${row.Month || 'month'}-${row.Currency || 'currency'}-${index}`
    },
    numericValue(value) {
      return Number(value) || 0
    },
    savingsPartitions(value) {
      const savings = value && typeof value === 'object'
        ? value
        : { refundable: value, non_refundable: 0 }
      return [
        {
          label: 'Refundable',
          raw: savings.refundable,
          value: this.formatCurrency(savings.refundable, this.kpis.Currency || this.filters.currency),
        },
        {
          label: 'Non-refundable',
          raw: savings.non_refundable,
          value: this.formatCurrency(savings.non_refundable, this.kpis.Currency || this.filters.currency),
        },
      ]
    },
    percentagePartitions(value) {
      const percentages = value && typeof value === 'object'
        ? value
        : { refundable: value, non_refundable: 0 }
      return [
        {
          label: 'Refundable',
          raw: percentages.refundable,
          value: this.formatPercent(percentages.refundable),
        },
        {
          label: 'Non-refundable',
          raw: percentages.non_refundable,
          value: this.formatPercent(percentages.non_refundable),
        },
      ]
    },
    percentageBarWidth(value) {
      const percentage = Math.max(0, Number(value) || 0)
      const scaled = Math.min(100, (percentage / 25) * 100)
      return `${scaled}%`
    },
    formatNumber(value) {
      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
      }).format(Number(value) || 0)
    },
    formatPercent(value) {
      return `${(Number(value) || 0).toFixed(2)}%`
    },
    formatCurrency(value, currency = 'USD') {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value) || 0)
      } catch (error) {
        return `${currency} ${this.formatAmount(value)}`
      }
    },
    formatAmount(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value) || 0)
    },
    columnClass(key) {
      return {
        'numeric-cell': [
          'TotalBookings',
          'CheaperBookings',
          'TotalOriginalCost',
          'TotalRefundableCost',
          'TotalNonRefundableCost',
          'TotalProfit',
          'ProfitPercentage',
        ].includes(key),
        'savings-cell': key === 'TotalProfit',
        'percentage-column': key === 'ProfitPercentage',
      }
    },
    goBack() {
      this.$router.push({ name: 'priceCheckBookings' })
    },
  },
}
</script>

<style scoped>
.price-check-reports-page {
  background: #f6f8fb;
  min-height: calc(100vh - 70px);
  padding: 18px 18px 28px;
}

.reports-header {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.reports-header h3 {
  color: #172033;
  font-size: 25px;
  font-weight: 700;
  margin: 0;
}

.filter-panel,
.reports-panel {
  background: #fff;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(22, 32, 51, 0.06);
}

.filter-panel {
  margin-bottom: 16px;
  padding: 18px 20px;
}

.filter-grid {
  align-items: end;
  display: grid;
  gap: 16px 14px;
  grid-template-columns: repeat(5, minmax(150px, 1fr));
}

.filter-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.filter-field label {
  color: #253247;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 7px;
}

.filter-field select {
  background: #fbfdff;
  border: 1px solid #cbd7e5;
  border-radius: 6px;
  color: #172033;
  font-size: 14px;
  height: 40px;
  outline: none;
  padding: 8px 12px;
  width: 100%;
}

.filter-field select:focus {
  border-color: #378ad6;
  box-shadow: 0 0 0 3px rgba(55, 138, 214, 0.12);
}

.filter-actions {
  align-items: end;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  min-width: 0;
}

.filter-actions :deep(.btn) {
  border-radius: 6px;
  font-weight: 600;
  height: 40px;
  min-width: 88px;
}

.kpi-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 16px;
}

.kpi-card {
  align-items: flex-start;
  background: #fff;
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  box-shadow: 0 8px 22px rgba(22, 32, 51, 0.05);
  display: flex;
  gap: 13px;
  min-height: 112px;
  padding: 17px 18px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.kpi-card:hover {
  border-color: #c5d6ea;
  box-shadow: 0 12px 28px rgba(22, 32, 51, 0.08);
  transform: translateY(-1px);
}

.kpi-icon {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  flex: 0 0 40px;
  font-size: 22px;
  font-weight: 800;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.kpi-icon :deep(svg) {
  height: 22px;
  stroke-width: 2.8;
  width: 22px;
}

.kpi-icon.primary {
  background: #dcebff;
  color: #005fc4;
}

.kpi-icon.success {
  background: #d9f3e4;
  color: #08783b;
}

.kpi-icon.info {
  background: #dff0ff;
  color: #075ca8;
}

.kpi-icon.money {
  background: #e6ebf2;
  color: #1c334f;
}

.kpi-icon.trend {
  background: #fff0c9;
  color: #9a5b00;
}

.kpi-copy {
  min-width: 0;
}

.kpi-copy span {
  color: #5b6b82;
  display: block;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.kpi-copy strong {
  color: #152238;
  display: block;
  font-size: 23px;
  font-weight: 800;
  line-height: 1.15;
  margin-bottom: 8px;
  overflow-wrap: anywhere;
}

.kpi-copy strong.savings-value {
  color: #168a4a;
}

.partition-line {
  align-items: baseline;
  display: flex !important;
  gap: 8px;
  justify-content: space-between;
  margin: 0 0 4px !important;
  min-width: 190px;
  text-transform: none !important;
}

.partition-line b,
.partition-cell b {
  color: #5b6b82;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.partition-line em,
.partition-cell em {
  font-style: normal;
  font-variant-numeric: tabular-nums;
}

.kpi-copy small {
  color: #728299;
  display: block;
  font-size: 12px;
  line-height: 1.3;
}

.kpi-loading {
  animation: kpi-pulse 1.1s ease-in-out infinite;
  background: linear-gradient(90deg, #e8eef6, #f7fafd, #e8eef6);
  background-size: 220% 100%;
  border-radius: 6px;
  display: block;
  height: 26px;
  width: 132px;
}

.reports-panel {
  padding: 0;
  overflow: hidden;
  border-radius: 10px;
}

.panel-title {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #e3ebf4;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin: 0;
  padding: 18px 20px 14px;
}

.panel-title h4 {
  color: #172033;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 3px;
}

.panel-title p {
  color: #667085;
  font-size: 13px;
  margin: 0;
}

.panel-title span,
.status-text {
  color: #65758b;
  font-size: 13px;
  margin: 0;
}

.status-text,
.error-text {
  padding: 18px;
}

.error-text {
  color: #dc2626;
  margin: 0;
}

.report-table-wrap {
  margin: 16px 18px 18px;
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  max-height: 520px;
  overflow-x: auto;
  overflow-y: auto;
}

.report-table {
  background: #fff;
  border-collapse: collapse;
  margin: 0;
  table-layout: auto;
  width: 100%;
  min-width: 1080px;
}

.report-table th {
  background: #f5f7fc;
  border-bottom: 1px solid #d9e2ef;
  color: #344054;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  line-height: 1.25;
  padding: 12px 14px;
  position: sticky;
  top: 0;
  text-transform: uppercase;
  white-space: nowrap;
  z-index: 2;
}

.report-table th:first-child {
  border-top-left-radius: 6px;
}

.report-table th:last-child {
  border-top-right-radius: 6px;
}

.report-table td {
  border-bottom: 1px solid #edf1f6;
  border-right: 0;
  color: #24364f;
  font-size: 13px;
  font-weight: 500;
  height: 46px;
  padding: 10px 14px;
  vertical-align: middle;
  white-space: nowrap;
  transition: background-color 0.16s ease;
}

.report-table td:last-child,
.report-table th:last-child {
  border-right: 0;
}

.report-table td.numeric-cell {
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.report-table th.numeric-cell {
  text-align: right;
}

.report-table tbody tr:hover {
  background: #f8fafc;
}

.report-table tbody tr:last-child td {
  border-bottom: 0;
}

.savings-positive {
  color: #168a4a;
  font-weight: 700;
}

.savings-zero {
  color: #667085;
  font-weight: 600;
}

.savings-negative {
  color: #c2410c;
  font-weight: 700;
}

.partition-cell {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  min-width: 190px;
  text-align: left;
}

.partition-cell span {
  align-items: baseline;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.percentage-cell {
  align-items: flex-end;
  display: inline-flex;
  flex-direction: column;
  gap: 5px;
  min-width: 86px;
}

.percentage-cell span {
  color: #24364f;
  font-variant-numeric: tabular-nums;
  font-weight: 650;
}

.partition-percentage-cell {
  gap: 7px;
  min-width: 150px;
}

.partition-percentage-cell span {
  align-items: flex-end;
  display: grid;
  gap: 4px;
  grid-template-columns: minmax(82px, 1fr) auto;
  width: 100%;
}

.partition-percentage-cell span > b {
  color: #5b6b82;
  font-size: 10px;
  font-weight: 800;
  text-align: left;
  text-transform: uppercase;
}

.partition-percentage-cell span > em {
  color: #24364f;
  font-style: normal;
  font-weight: 800;
}

.partition-percentage-cell i {
  grid-column: 1 / -1;
  justify-self: end;
}

.percentage-cell i {
  background: #e8edf3;
  border-radius: 999px;
  display: block;
  height: 4px;
  overflow: hidden;
  width: 72px;
}

.percentage-cell i b {
  background: #20a464;
  border-radius: 999px;
  display: block;
  height: 100%;
}

.table-skeleton {
  display: grid;
  gap: 10px;
  padding: 18px;
}

.table-skeleton span {
  animation: kpi-pulse 1.1s ease-in-out infinite;
  background: linear-gradient(90deg, #e8eef6, #f7fafd, #e8eef6);
  background-size: 220% 100%;
  border-radius: 8px;
  display: block;
  height: 42px;
}

.pagination-bar {
  align-items: center;
  border-top: 1px solid #e3ebf4;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 18px 16px;
}

.pagination-summary {
  color: #667085;
  font-size: 13px;
  font-weight: 600;
}

.pagination-controls {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  justify-content: flex-end;
}

.pagination-controls label {
  align-items: center;
  color: #475467;
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  gap: 7px;
  margin: 0;
}

.pagination-controls select {
  background: #fff;
  border: 1px solid #cbd7e5;
  border-radius: 6px;
  color: #172033;
  height: 32px;
  padding: 4px 28px 4px 9px;
}

.pagination-controls span {
  color: #475467;
  font-size: 13px;
  font-weight: 700;
  min-width: 92px;
  text-align: center;
}

.pagination-controls :deep(.btn) {
  border-radius: 6px;
  font-weight: 700;
  min-width: 76px;
}

@media (max-width: 900px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-actions {
    justify-content: flex-start;
  }

  .pagination-bar {
    align-items: flex-start;
    flex-direction: column;
  }

  .pagination-controls {
    justify-content: flex-start;
  }
}

@media (max-width: 560px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

@keyframes kpi-pulse {
  to {
    background-position: -220% 0;
  }
}
</style>
